import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, XCircle, RefreshCw,
  ArrowLeft, Eye, Wand2, X, ChevronLeft, ChevronRight, Scale, ListChecks, Clock
} from 'lucide-react';

const SEVERITIES = [
  { value: '', label: 'Toutes sévérités' },
  { value: 'haute', label: 'Haute (mauvaise réponse)' },
  { value: 'moyenne', label: 'Moyenne (article incorrect)' },
  { value: 'faible', label: 'Faible (formulation)' },
];
const STATUSES = [
  { value: 'pending', label: 'À traiter' },
  { value: 'approved', label: 'Marqué pour suite' },
  { value: 'rejected', label: 'Rejeté (faux positif)' },
  { value: 'fixed', label: 'Corrigé' },
  { value: '', label: 'Tous statuts' },
];
const THEMES = [
  { code: '', name: 'Tous les thèmes' },
  { code: 'R', name: 'R · La route' },
  { code: 'C', name: 'C · La circulation' },
  { code: 'L', name: 'L · Le conducteur' },
  { code: 'U', name: 'U · Les autres usagers' },
  { code: 'D', name: 'D · Notions diverses' },
  { code: 'M', name: 'M · Mécanique' },
  { code: 'P', name: 'P · Équipements' },
  { code: 'E', name: 'E · Environnement' },
  { code: 'S', name: 'S · Premiers secours' },
];
const PAGE_SIZE = 10;

const sevColor = { haute: '#cf2031', moyenne: '#f59e0b', faible: '#1d4ed8' };
const sevSoft = { haute: '#fff1f2', moyenne: '#fff7ed', faible: '#eff6ff' };
const sevIcon = { haute: AlertTriangle, moyenne: AlertCircle, faible: Info };

export default function AdminAudit() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [severity, setSeverity] = useState('haute');
  const [status, setStatus] = useState('pending');
  const [theme, setTheme] = useState('');
  const [page, setPage] = useState(0);
  const [actingId, setActingId] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const s = await api.getAdminAuditSummary();
      setSummary(s);
    } catch (e) {
      toast.error('Erreur de chargement du résumé');
    }
  }, []);

  const loadFindings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminAuditFindings({
        severity: severity || undefined,
        status: status || undefined,
        theme: theme || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setFindings(res.findings || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error('Erreur de chargement des erreurs');
    } finally {
      setLoading(false);
    }
  }, [severity, status, theme, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadFindings(); }, [loadFindings]);

  const onFilter = (fn) => { fn(); setPage(0); };

  const applyAnswer = async (f) => {
    if (!window.confirm(`Appliquer la correction : changer la bonne réponse ${f.current_correct} → ${f.proposed_correct} ?`)) return;
    setActingId(f.id);
    try {
      await api.applyAuditFinding(f.id, { apply_correct_answer: true });
      toast.success('Correction appliquée à la question');
      await Promise.all([loadFindings(), loadSummary()]);
    } catch (e) {
      toast.error('Échec de l\'application');
    } finally { setActingId(null); }
  };

  const applyLegal = async (f) => {
    if (!window.confirm(`Mettre à jour la référence légale : "${f.current_legal || '(vide)'}" → "${f.proposed_legal}" ?`)) return;
    setActingId(f.id);
    try {
      await api.applyAuditFinding(f.id, { apply_legal_reference: true });
      toast.success('Référence légale mise à jour');
      await Promise.all([loadFindings(), loadSummary()]);
    } catch (e) {
      toast.error('Échec de l\'application');
    } finally { setActingId(null); }
  };

  const applyBoth = async (f) => {
    if (!window.confirm(`Appliquer les 2 corrections (réponse + référence légale) ?`)) return;
    setActingId(f.id);
    try {
      await api.applyAuditFinding(f.id, { apply_correct_answer: true, apply_legal_reference: true });
      toast.success('Corrections appliquées');
      await Promise.all([loadFindings(), loadSummary()]);
    } catch (e) {
      toast.error('Échec');
    } finally { setActingId(null); }
  };

  const reject = async (f) => {
    const note = window.prompt('Note (optionnelle) - pourquoi rejeter cette suggestion ?', '');
    if (note === null) return;
    setActingId(f.id);
    try {
      await api.rejectAuditFinding(f.id, { note });
      toast.success('Suggestion rejetée (la question reste inchangée)');
      await Promise.all([loadFindings(), loadSummary()]);
    } catch (e) {
      toast.error('Échec');
    } finally { setActingId(null); }
  };

  const approve = async (f) => {
    setActingId(f.id);
    try {
      await api.approveAuditFinding(f.id);
      toast.success('Marqué pour traitement ultérieur');
      await Promise.all([loadFindings(), loadSummary()]);
    } catch (e) {
      toast.error('Échec');
    } finally { setActingId(null); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="admin-audit-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button onClick={() => navigate('/admin')} variant="ghost" size="sm" className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back-office
          </Button>
          <h1 className="text-3xl font-black flex items-center gap-2">
            <ListChecks className="w-7 h-7 text-[hsl(var(--primary))]" />
            Audit IA des questions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vérifications juridiques automatiques. Validez ou rejetez chaque suggestion.
          </p>
        </div>
        <Button onClick={() => { loadSummary(); loadFindings(); }} variant="outline" size="sm" data-testid="refresh-audit">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Synthèse */}
      {summary?.has_report && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="audit-summary">
          <SummaryCard label="Total audité" value={summary.report.total_audited} sub="questions" color="#1d4ed8" />
          <SummaryCard
            label="Erreurs hautes"
            value={summary.counts.haute_pending}
            sub={`${summary.counts.haute_fixed} corrigées · ${summary.counts.haute_rejected} rejetées`}
            color={sevColor.haute}
          />
          <SummaryCard
            label="Erreurs moyennes"
            value={summary.counts.moyenne_pending}
            sub={`${summary.counts.moyenne_fixed} corrigées · ${summary.counts.moyenne_rejected} rejetées`}
            color={sevColor.moyenne}
          />
          <SummaryCard
            label="OK validées"
            value={summary.report.stats.ok}
            sub={`sur ${summary.report.total_audited} (${Math.round(100 * summary.report.stats.ok / summary.report.total_audited)}%)`}
            color="#16a34a"
          />
        </div>
      )}

      {/* Filtres */}
      <div className="mc-card p-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <select
            value={severity}
            onChange={(e) => onFilter(() => setSeverity(e.target.value))}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
            data-testid="audit-filter-severity"
          >
            {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={status}
            onChange={(e) => onFilter(() => setStatus(e.target.value))}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
            data-testid="audit-filter-status"
          >
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={theme}
            onChange={(e) => onFilter(() => setTheme(e.target.value))}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
            data-testid="audit-filter-theme"
          >
            {THEMES.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
        </div>
      </div>

      {/* Findings */}
      {loading ? (
        <div className="mc-card p-10 text-center" data-testid="audit-loading">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[hsl(var(--primary))] mb-2" />
          <p className="text-sm text-muted-foreground">Chargement…</p>
        </div>
      ) : findings.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">
          Aucune erreur ne correspond aux filtres. Tout est OK !
        </div>
      ) : (
        <div className="space-y-3">
          {findings.map(f => {
            const SevIcon = sevIcon[f.severity] || Info;
            const answerChanged = f.proposed_correct && f.proposed_correct !== f.current_correct;
            const legalChanged = f.proposed_legal && f.proposed_legal !== f.current_legal;
            return (
              <div
                key={f.id}
                className="mc-card p-5 border-l-4 transition-colors"
                style={{ borderLeftColor: sevColor[f.severity] }}
                data-testid={`audit-finding-${f.id}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest"
                      style={{ background: sevSoft[f.severity], color: sevColor[f.severity] }}
                    >
                      <SevIcon className="w-3 h-3" /> {f.severity}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">
                      Thm {f.theme}
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground">{f.question_id}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {f.status === 'pending' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
                        <Clock className="w-3 h-3" /> à traiter
                      </span>
                    )}
                    {f.status === 'fixed' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#16a34a]">
                        <CheckCircle2 className="w-3 h-3" /> corrigé
                      </span>
                    )}
                    {f.status === 'rejected' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
                        <X className="w-3 h-3" /> rejeté (faux positif)
                      </span>
                    )}
                  </div>
                </div>

                {/* Question */}
                <p className="text-sm font-semibold mb-2" data-testid={`audit-q-${f.id}`}>{f.question_text}</p>
                <div className="grid sm:grid-cols-2 gap-1 text-xs mb-3">
                  {(f.choices || []).map((c, i) => {
                    const letter = String.fromCharCode(65 + i);
                    const isCurrent = letter === f.current_correct;
                    const isProposed = letter === f.proposed_correct;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'flex items-start gap-1.5 p-1.5 rounded',
                          isCurrent && 'bg-[#fff1f2] text-[#b42318] font-bold border border-[#fecdd3]',
                          isProposed && !isCurrent && 'bg-[#eaf8ef] text-[#16a34a] font-bold border border-[#c8edd6]',
                          isProposed && isCurrent && 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] font-bold'
                        )}
                      >
                        <span className="font-black">{letter}.</span>
                        <span className="flex-1">{c}</span>
                        {isCurrent && !isProposed && <span className="text-[9px] font-black">ACTUEL</span>}
                        {isProposed && !isCurrent && <span className="text-[9px] font-black">PROPOSÉ</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Issue */}
                {f.issue && (
                  <div className="rounded-lg p-3 mb-3 text-xs" style={{ background: sevSoft[f.severity] }}>
                    <div className="font-black uppercase text-[10px] tracking-widest mb-0.5" style={{ color: sevColor[f.severity] }}>
                      Problème détecté par l'IA
                    </div>
                    <p className="text-foreground/85">{f.issue}</p>
                  </div>
                )}

                {/* Diff */}
                <div className="grid sm:grid-cols-2 gap-2 text-xs mb-3">
                  {answerChanged && (
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Bonne réponse</div>
                      <div className="flex items-center gap-2 font-mono">
                        <span className="text-[#b42318] line-through">{f.current_correct}</span>
                        <span>→</span>
                        <span className="text-[#16a34a] font-black">{f.proposed_correct}</span>
                      </div>
                    </div>
                  )}
                  {legalChanged && (
                    <div className="rounded-lg border border-border p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Référence légale</div>
                      <div className="text-[11px]">
                        <div className="text-[#b42318] line-through">{f.current_legal || '(vide)'}</div>
                        <div className="text-[#16a34a] font-bold">{f.proposed_legal || '(vide)'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {f.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                    {answerChanged && legalChanged && (
                      <Button
                        onClick={() => applyBoth(f)}
                        disabled={actingId === f.id}
                        className="mc-btn-primary h-9"
                        data-testid={`apply-both-${f.id}`}
                      >
                        <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                        Appliquer les 2 corrections
                      </Button>
                    )}
                    {answerChanged && (
                      <Button
                        onClick={() => applyAnswer(f)}
                        disabled={actingId === f.id}
                        variant={answerChanged && legalChanged ? 'outline' : 'default'}
                        size="sm"
                        className={cn('h-9', !(answerChanged && legalChanged) && 'mc-btn-primary')}
                        data-testid={`apply-answer-${f.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                        Corriger la réponse
                      </Button>
                    )}
                    {legalChanged && (
                      <Button
                        onClick={() => applyLegal(f)}
                        disabled={actingId === f.id}
                        variant="outline"
                        size="sm"
                        className="h-9"
                        data-testid={`apply-legal-${f.id}`}
                      >
                        <Scale className="w-3.5 h-3.5 mr-1.5" />
                        Corriger l'article
                      </Button>
                    )}
                    <Button
                      onClick={() => reject(f)}
                      disabled={actingId === f.id}
                      variant="outline"
                      size="sm"
                      className="h-9 text-[#b42318] border-[#fecdd3] hover:bg-[#fff1f2]"
                      data-testid={`reject-${f.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" />
                      Rejeter (faux positif)
                    </Button>
                    <Button
                      onClick={() => approve(f)}
                      disabled={actingId === f.id}
                      variant="ghost"
                      size="sm"
                      className="h-9 ml-auto text-muted-foreground"
                      data-testid={`approve-${f.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      Voir plus tard
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="audit-pagination">
          <Button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} variant="outline" size="sm">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-bold">Page {page + 1} / {totalPages}</span>
          <Button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} variant="outline" size="sm">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, sub, color }) => (
  <div className="mc-card p-4 border-l-4" style={{ borderLeftColor: color }} data-testid={`audit-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-3xl font-black mt-1" style={{ color }}>{value}</div>
    <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
  </div>
);
